"use client";

import { JSX, useEffect, useMemo, useRef } from "react";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import type { ProcessStatus } from "@/components/charts/process-status";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  categorySvgKey,
  legendIndexOfCategory,
  resolveCategories,
  resolveCategoryColor,
} from "./shared/category-resolve";
import type { ModuleCategory } from "@/types/modules";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { importsTokenScope } from "./imports-token-scope";

export type StateMachineNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  status?: ProcessStatus;
  categoryId?: string;
};

export type StateMachineTransitionType = "normal" | "negative" | "retry";

export type StateMachineTransition = {
  from: string;
  to: string;
  label: string;
  type?: StateMachineTransitionType;
  transitionCategoryId?: string;
};

export type StateMachineData = {
  states: StateMachineNode[];
  transitions: StateMachineTransition[];
  categories?: ModuleCategory[];
  transitionCategories?: ModuleCategory[];
};

function legacyTransitionCategories(t: Translate): ModuleCategory[] {
  return [
    { id: "normal", label: t("Normal transition"), color: "var(--status-2)" },
    { id: "negative", label: t("Negative branch"), color: "var(--status-neutral)" },
    { id: "retry", label: t("Retry loop"), color: "var(--status-neutral)" },
  ];
}

const DEFAULT_DATA: StateMachineData = {
  states: [
    { id: "draft", label: "Draft", x: 12, y: 1, status: "done" },
    { id: "review", label: "Review", x: 38, y: 1, status: "active" },
    { id: "approved", label: "Approved", x: 65, y: 1, status: "pending" },
    { id: "rejected", label: "Rejected", x: 38, y: 2, status: "pending" },
    { id: "published", label: "Published", x: 90, y: 1, status: "pending" },
  ],
  transitions: [
    { from: "draft", to: "review", label: "submit", type: "normal" },
    { from: "review", to: "approved", label: "approve", type: "normal" },
    { from: "approved", to: "published", label: "publish", type: "normal" },
    { from: "review", to: "rejected", label: "reject", type: "negative" },
    { from: "rejected", to: "draft", label: "revise", type: "retry" },
  ],
};

export default function ModuleStateMachine({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: StateMachineData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const states = data.states ?? [];
  const transitions = data.transitions ?? [];
  const stateCategories = data.categories;
  const transitionCategories = data.transitionCategories;
  const statesFree = Boolean(stateCategories && stateCategories.length > 0);
  const resolvedTransitions = resolveCategories(
    transitionCategories,
    legacyTransitionCategories(t),
  );
  const transitionsFree = resolvedTransitions.free;

  const selection = useSelectionGroup();
  const state = useHoverSelect<string>({ group: selection });
  const effective = state.effective;
  const transitionLegend = useLegendHoverSelect({ group: selection });
  const stateLegend = useLegendHoverSelect({ group: selection });
  const maskId = useSvgId("sm-mask");
  const dotsId = useSvgId("sm-dots");
  const arrowPrefix = useSvgId("sm-arrow");
  const hoverGesture = useHoverGesture();

  const W = 760;
  const H = 260;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [states, transitions]);
  const padX = 60;
  const chartW = W - padX * 2;
  const yTop = 80;
  const rowOffset = 70;
  const xFor = (x: number) => padX + (x / 100) * chartW;
  const yFor = (y: number) => yTop + (y - 1) * rowOffset;
  const nodeR = 26;

  const stateById = useMemo(
    () =>
      Object.fromEntries(states.map((s) => [s.id, s])) as Record<string, StateMachineNode>,
    [states],
  );

  const transitionKey = (tr: StateMachineTransition) =>
    transitionsFree ? tr.transitionCategoryId : tr.type;

  const legendItems = resolvedTransitions.categories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    color: cat.color,
    value: transitions.filter((tr) => transitionKey(tr) === cat.id).length,
    ...(transitionsFree ? {} : { dashed: cat.id !== "normal" }),
  }));
  const stateLegendItems = statesFree
    ? (stateCategories as ModuleCategory[]).map((cat) => ({
        id: cat.id,
        label: cat.label,
        color: cat.color,
        value: states.filter((s) => s.categoryId === cat.id).length,
      }))
    : [];
  const legFilter =
    transitionLegend.filter !== null
      ? legendItems[transitionLegend.filter]?.id ?? null
      : null;
  const stateLegFilter =
    stateLegend.filter !== null
      ? stateLegendItems[stateLegend.filter]?.id ?? null
      : null;
  const selectedStateId = state.selected;
  const stateLegendSelectedIndex =
    stateLegend.selected ??
    legendIndexOfCategory(
      stateLegendItems,
      selectedStateId ? stateById[selectedStateId]?.categoryId : null,
    );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activeState = effective
    ? states.find((s) => s.id === effective) ?? null
    : null;
  const activeStatusMeta =
    activeState && statesFree
      ? stateLegendItems.find((li) => li.id === activeState.categoryId) ?? null
      : null;
  const outgoingTransitions = activeState
    ? transitions.filter((tr) => tr.from === activeState.id)
    : [];
  // All three surfaces feed one scroll target: a transition row centres the
  // state its first transition leaves, a state-category row the first state of
  // that category, and a state picked in the graph is only pulled in when it
  // sits outside the viewport. Keying the effect on the resolved id instead of
  // the rebuilt arrays keeps it from firing on unrelated renders, which is what
  // makes re-picking the same row scroll again.
  const scrollTransitionCategory =
    transitionLegend.selected !== null
      ? legendItems[transitionLegend.selected]?.id ?? null
      : null;
  const scrollStateCategory =
    stateLegend.selected !== null
      ? stateLegendItems[stateLegend.selected]?.id ?? null
      : null;
  const scrollStateId =
    scrollTransitionCategory !== null
      ? transitions.find((tr) => transitionKey(tr) === scrollTransitionCategory)
          ?.from ?? null
      : scrollStateCategory !== null
        ? states.find((s) => s.categoryId === scrollStateCategory)?.id ?? null
        : selectedStateId;
  const scrollAlign = selectedStateId !== null ? "nearest" : "center";
  useEffect(() => {
    if (scrollStateId === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-part-index="${scrollStateId}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollStateId, scrollAlign]);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <defs>
            <pattern id={dotsId} width="18" height="18" patternUnits="userSpaceOnUse">
              <circle cx="9" cy="9" r="0.75" fill="var(--grid)" />
            </pattern>
            <marker
              id={`${arrowPrefix}-normal`}
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M 0,0 L 7,4 L 0,8 z" fill="var(--status-2)" />
            </marker>
            <marker
              id={`${arrowPrefix}-negative`}
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M 0,0 L 7,4 L 0,8 z" fill="var(--status-neutral)" />
            </marker>
            <marker
              id={`${arrowPrefix}-retry`}
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M 0,0 L 7,4 L 0,8 z" fill="var(--status-neutral)" />
            </marker>
            {transitionsFree &&
              resolvedTransitions.categories.map((cat) => (
                <marker
                  key={cat.id}
                  id={`${arrowPrefix}-${categorySvgKey(cat.id)}`}
                  markerWidth="8"
                  markerHeight="8"
                  refX="7"
                  refY="4"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M 0,0 L 7,4 L 0,8 z" fill={cat.color} />
                </marker>
              ))}
          </defs>
          <g mask={`url(#${maskId})`}>
            <rect width={W} height={H} fill={`url(#${dotsId})`} opacity="0.5" />
          </g>

          {transitions.map((tr, idx) => {
            const from = stateById[tr.from];
            const to = stateById[tr.to];
            if (!from || !to) return null;
            const x1 = xFor(from.x);
            const y1 = yFor(from.y);
            const x2 = xFor(to.x);
            const y2 = yFor(to.y);
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;
            const px1 = x1 + (dx / len) * nodeR;
            const py1 = y1 + (dy / len) * nodeR;
            const px2 = x2 - (dx / len) * (nodeR + 6);
            const py2 = y2 - (dy / len) * (nodeR + 6);
            const dim =
              (effective !== null &&
                effective !== tr.from &&
                effective !== tr.to) ||
              (legFilter !== null && legFilter !== transitionKey(tr));
            const isRetry = !transitionsFree && tr.type === "retry";

            let d: string;
            let labelX: number;
            let labelY: number;
            let labelAnchor: "start" | "middle" | "end" = "middle";
            if (isRetry) {
              const mx = (x1 + x2) / 2;
              const my = Math.min(y1, y2) - 55;
              d = `M ${px1},${py1} C ${x1},${my + 20} ${x2},${my + 20} ${px2},${py2}`;
              labelX = mx;
              labelY = my + 10;
            } else if (Math.abs(y1 - y2) < 1) {
              d = `M ${px1},${py1} L ${px2},${py2}`;
              labelX = (px1 + px2) / 2;
              labelY = y1 - 8;
            } else {
              const mx = px1 + (px2 - px1) * 0.55;
              const backward = px2 < px1;
              d = `M ${px1},${py1} C ${mx},${py1} ${mx},${py2} ${px2},${py2}`;
              labelX = backward ? mx - 6 : mx + 6;
              labelY = (py1 + py2) / 2 + 4;
              labelAnchor = backward ? "end" : "start";
            }

            const transitionCatId = transitionKey(tr);
            const transitionCat = transitionsFree
              ? resolvedTransitions.categories.find((c) => c.id === transitionCatId)
              : undefined;
            const stroke = transitionsFree
              ? transitionCat?.color ?? "var(--chart-2)"
              : tr.type === "normal"
                ? "var(--status-2)"
                : "var(--status-neutral)";
            const dasharray = transitionsFree
              ? "0"
              : tr.type === "negative"
                ? "4 4"
                : tr.type === "retry"
                  ? "3 4"
                  : "0";
            const markerEnd = transitionsFree
              ? transitionCat
                ? `url(#${arrowPrefix}-${categorySvgKey(transitionCat.id)})`
                : `url(#${arrowPrefix}-normal)`
              : tr.type === "normal"
                ? `url(#${arrowPrefix}-normal)`
                : tr.type === "negative"
                  ? `url(#${arrowPrefix}-negative)`
                  : `url(#${arrowPrefix}-retry)`;

            return (
              <g
                key={idx}
                opacity={dim ? 0.15 : 1}
                style={{ transition: "opacity 0.15s" }}
              >
                <path
                  d={d}
                  fill="none"
                  stroke={stroke}
                  strokeWidth="1.3"
                  strokeDasharray={dasharray}
                  strokeLinecap="round"
                  markerEnd={markerEnd}
                />
                <foreignObject
                  x={labelAnchor === "end" ? labelX - 80 : labelAnchor === "middle" ? labelX - 40 : labelX}
                  y={labelY - 10}
                  width={80}
                  height={28}
                  overflow="visible"
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        labelAnchor === "end"
                          ? "flex-end"
                          : labelAnchor === "middle"
                            ? "center"
                            : "flex-start",
                      height: "100%",
                      color: "var(--subtle)",
                      fontSize: "10px",
                      lineHeight: 1.15,
                      textAlign:
                        labelAnchor === "end"
                          ? "right"
                          : labelAnchor === "middle"
                            ? "center"
                            : "left",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {stripInlineMarkdown(tr.label)}
                  </div>
                </foreignObject>
              </g>
            );
          })}

          {states.map((s) => {
            const cx = xFor(s.x);
            const cy = yFor(s.y);
            const isActive = !statesFree && s.status === "active";
            const isDone = !statesFree && s.status === "done";
            const isPending = !statesFree && s.status === "pending";
            const isActiveState = effective === s.id;
            const isSelected = selectedStateId === s.id;
            const stateColor = resolveCategoryColor(
              s.categoryId,
              stateCategories,
              "var(--chart-2)",
            );
            const dim =
              (effective !== null && !isActiveState) ||
              (statesFree &&
                stateLegFilter !== null &&
                stateLegFilter !== s.categoryId);

            return (
              <g
                key={s.id}
                ref={(el) => {
                  anchorRefs.current[s.id] = el;
                }}
                data-module-type="state-machine"
                data-part-index={s.id}
                data-part-label={s.label}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) state.setHovered(s.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) state.setHovered(null);
                }}
                onClick={(e) =>
                  state.select(s.id, {
                    kind: "node",
                    id: s.id,
                    element: e.currentTarget,
                  })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.4 : 1}
              >
                {isSelected && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={nodeR + 4}
                    fill="none"
                    stroke="var(--module-accent)"
                    strokeWidth="2"
                  />
                )}
                <circle
                  cx={cx}
                  cy={cy}
                  r={nodeR}
                  fill="var(--card)"
                  stroke={
                    statesFree
                      ? stateColor
                      : isActive
                        ? "var(--status-1)"
                        : isDone
                          ? "var(--status-2)"
                          : "var(--status-neutral)"
                  }
                  strokeWidth={isSelected ? "2.5" : "1.5"}
                  strokeDasharray={isPending ? "3 3" : "0"}
                />
                <foreignObject
                  x={cx - nodeR + 4}
                  y={cy - nodeR + 4}
                  width={nodeR * 2 - 8}
                  height={nodeR * 2 - 8}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: isPending ? "var(--muted-foreground)" : "var(--foreground)",
                      fontSize: "11px",
                      fontWeight: 500,
                      lineHeight: 1.15,
                      textAlign: "center",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {stripInlineMarkdown(s.label)}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeState !== null}
        anchor={activeState ? anchorRefs.current[activeState.id] ?? null : null}
        contentKey={activeState ? String(activeState.id) : undefined}
        title={stripInlineMarkdown(activeState?.label) || undefined}
        rows={
          activeState
            ? [
                ...(activeStatusMeta
                  ? [{ color: activeStatusMeta.color, label: t("Status"), value: activeStatusMeta.label }]
                  : []),
                ...(outgoingTransitions.length > 0
                  ? [
                      {
                        color: "var(--muted-foreground)",
                        label:
                          outgoingTransitions.length > 1 ? t("Transitions") : t("Transition"),
                        value: outgoingTransitions.map((tr) => tr.label).join(", "),
                      },
                    ]
                  : []),
              ]
            : []
        }
      />

      {showLegend && statesFree && (
        <SwipeScroll>
          <Legend
            items={stateLegendItems}
            hoveredIndex={stateLegend.hovered}
            onHoverChange={stateLegend.setHovered}
            selectedIndex={stateLegendSelectedIndex}
            onSelect={stateLegend.select}
            onLabelRef={stateLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}

      {showLegend && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={transitionLegend.hovered}
            onHoverChange={transitionLegend.setHovered}
            selectedIndex={transitionLegend.selected}
            onSelect={transitionLegend.select}
            onLabelRef={transitionLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker variant="arrow" />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
